import type {TFunction} from 'i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {type Column} from '@/components/data-table'
import {INVOICE_ESTADO_TONE, type AdminInvoice} from '@/hooks/inci/cartera/use-cartera'
import {formatCopFull} from '@/lib/money'
import {useTranslation} from 'react-i18next'
import {FacturaRowActions} from '@/components/cartera/factura-action-dialogs'
const dateFormat = new Intl.DateTimeFormat('es-CO', {year: 'numeric', month: 'short', day: 'numeric'})

/** Callers add their own leading `empresa` and/or trailing `actions` column. */
export function facturaBaseColumns(t: TFunction): Column<AdminInvoice>[] {
  return [
    {
      key: 'numero',
      header: t('cartera.facturas.table.numero'),
      width: 'w-32',
      cell: (row) => <span className="block truncate font-mono text-xs text-foreground">{row.numero_factura}</span>,
    },
    {
      key: 'estado',
      header: t('cartera.facturas.table.estado'),
      width: 'w-32',
      cell: (row) => (
        <CeldaEstado
          chip={<Tag tone={INVOICE_ESTADO_TONE[row.estado]}>{t(`cartera.estado.${row.estado}`)}</Tag>}
          meta={
            row.estado === 'anulada' &&
            row.anulada_at &&
            t('cartera.facturas.anuladaLine', {
              date: dateFormat.format(new Date(row.anulada_at)),
              motivo: row.anulada_motivo ?? '',
            })
          }
        />
      ),
    },
    {
      key: 'vencimiento',
      header: t('cartera.facturas.table.vencimiento'),
      width: 'w-28',
      cell: (row) => (
        <span className="block truncate font-mono text-xs text-muted-foreground">
          {dateFormat.format(new Date(row.due_date))}
        </span>
      ),
    },
    {
      key: 'total',
      header: t('cartera.facturas.table.total'),
      width: 'w-28',
      cell: (row) => (
        <span className="font-mono text-sm text-foreground tabular-nums">{formatCopFull(row.total_cop)}</span>
      ),
    },
  ]
}

export function facturaActionsColumn(
  t: TFunction,
  onPagoManual: (row: AdminInvoice) => void,
  onAnular: (row: AdminInvoice) => void
): Column<AdminInvoice> {
  return {
    key: 'actions',
    header: '',
    width: 'w-44',
    className: 'text-right',
    cell: (row) => {
      const anulable = row.estado === 'emitida' || row.estado === 'vencida'
      return (
        <div className="flex flex-wrap items-center justify-end gap-1">
          {anulable && (
            <>
              <Button
                size="sm"
                variant="outline"
                data-testid={`cartera-factura-pago-manual-${row.id}`}
                onClick={() => onPagoManual(row)}>
                {t('cartera.facturas.pagoManual')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                data-testid={`cartera-factura-anular-${row.id}`}
                onClick={() => onAnular(row)}>
                {t('cartera.facturas.anular')}
              </Button>
            </>
          )}
          {row.estado === 'pagada' && (
            <span className="text-xs text-muted-foreground" data-testid={`cartera-factura-anular-bloqueada-${row.id}`}>
              {t('cartera.facturas.anularBlockedPagada')}
            </span>
          )}
        </div>
      )
    },
  }
}

export function useFacturaColumns(
  onAnular: (invoice: AdminInvoice) => void,
  onPagoManual: (invoice: AdminInvoice) => void,
  testPrefix: string
): Column<AdminInvoice>[] {
  const {t} = useTranslation()
  const [numero, estado, vencimiento, total] = facturaBaseColumns(t)
  return [
    numero,
    {
      key: 'empresa',
      header: t('cartera.facturas.table.empresa'),
      cell: (row) => <span className="block truncate font-medium text-foreground">{row.business_name}</span>,
    },
    estado,
    vencimiento,
    total,
    {
      key: 'actions',
      header: '',
      width: 'w-44',
      className: 'text-right',
      cell: (row) => (
        <FacturaRowActions invoice={row} onAnular={onAnular} onPagoManual={onPagoManual} testPrefix={testPrefix} />
      ),
    },
  ]
}
